import { create } from 'zustand';
import type { SceneData, NarrativeData, PlayerChoice, CreditsData } from '../types/story.types';

interface StoryState {
  // === Story Data (from Blazor) ===
  availableScenes: SceneData[];
  currentScene: SceneData | null;
  currentNarrative: NarrativeData | null;
  availableChoices: PlayerChoice[];
  gameState: Record<string, unknown>;

  // === Credits Data ===
  creditsData: CreditsData | null;
  isPlayingCredits: boolean;

  // === Donation Settings ===
  showDonation: boolean;
  donationUsername: string | null;

  // === Story Status ===
  isStoryLoaded: boolean;
  isStoryCompleted: boolean;
  isLoading: boolean;

  // === UI State ===
  isMenuOpen: boolean;
  isDialogueBoxVisible: boolean;
  isChoicePanelVisible: boolean;
  isTransitioning: boolean;

  // === Dialogue State ===
  isTypewriterComplete: boolean;
  canAdvance: boolean;

  // === Actions (called by Blazor via interop) ===
  setAvailableScenes: (scenes: SceneData[]) => void;
  setScene: (scene: SceneData) => void;
  setNarrative: (narrative: NarrativeData) => void;
  setChoices: (choices: PlayerChoice[]) => void;
  setGameState: (state: Record<string, unknown>) => void;
  setStoryCompleted: (completed: boolean) => void;
  playCredits: (data: CreditsData) => void;
  setLoading: (loading: boolean) => void;
  setDonationSettings: (showDonation: boolean, username: string | null) => void;

  // === UI Actions (called by React components) ===
  toggleMenu: () => void;
  openMenu: () => void;
  closeMenu: () => void;
  showDialogue: () => void;
  hideDialogue: () => void;
  showChoices: () => void;
  hideChoices: () => void;
  setTransitioning: (transitioning: boolean) => void;

  // === Dialogue Actions ===
  setTypewriterComplete: (complete: boolean) => void;
  setCanAdvance: (canAdvance: boolean) => void;

  // === Narrative Actions (trigger Blazor callbacks) ===
  advanceNarrative: () => void;
  selectChoice: (choiceId: string) => void;

  // === Utility ===
  reset: () => void;
}

const initialState = {
  availableScenes: [],
  currentScene: null,
  currentNarrative: null,
  availableChoices: [],
  gameState: {},
  creditsData: null,
  isPlayingCredits: false,
  showDonation: false,
  donationUsername: null,
  isStoryLoaded: false,
  isStoryCompleted: false,
  isLoading: true,
  isMenuOpen: false,
  isDialogueBoxVisible: true,
  isChoicePanelVisible: false,
  isTransitioning: false,
  isTypewriterComplete: false,
  canAdvance: false,
};

export const useStoryStore = create<StoryState>((set, get) => ({
  ...initialState,

  // === Blazor Data Updates ===
  setAvailableScenes: (scenes) => {
    console.log('[StoryStore] Available scenes updated:', scenes.length);

    set({
      availableScenes: scenes,
    });
  },

  setScene: (scene) => {
    console.log('[StoryStore] Scene updated:', scene.title);

    set({
      currentScene: scene,
      isLoading: false,
      isStoryLoaded: true,
      isTransitioning: false,
      isPlayingCredits: false,
      creditsData: null,
    });
  },

  setNarrative: (narrative) => {
    console.log('[StoryStore] Narrative updated:', narrative.id);

    set({
      currentNarrative: narrative,
      isTypewriterComplete: false,
      canAdvance: false, // Will be set by AudioManager based on audio completion
      isDialogueBoxVisible: true,
      isChoicePanelVisible: narrative.hasChoices,
      isTransitioning: false, // Clear transition state when narrative updates
    });
  },

  setChoices: (choices) => {
    console.log('[StoryStore] Choices updated:', choices.length);

    set({
      availableChoices: choices,
      isChoicePanelVisible: choices.length > 0,
      canAdvance: false, // Must choose, can't advance
    });
  },

  setGameState: (state) => {
    set({ gameState: state });
  },

  setStoryCompleted: (completed) => {
    console.log('[StoryStore] Story completed:', completed);

    set({
      isStoryCompleted: completed,
      isDialogueBoxVisible: false,
      isChoicePanelVisible: false,
    });
  },

  playCredits: (data) => {
    console.log('[StoryStore] Playing credits');
    set({
      creditsData: data,
      isPlayingCredits: true,
      isDialogueBoxVisible: false,
      isChoicePanelVisible: false,
      currentNarrative: null // Clear narrative
    });
  },

  setLoading: (loading) => {
    set({ isLoading: loading });
  },

  setDonationSettings: (showDonation, username) => {
    console.log('[StoryStore] Donation settings updated:', { showDonation, username });

    set({
      showDonation,
      donationUsername: username,
    });
  },

  // === UI Actions ===
  toggleMenu: () => {
    const { isMenuOpen } = get();
    set({ isMenuOpen: !isMenuOpen });
  },

  openMenu: () => {
    set({ isMenuOpen: true });
  },

  closeMenu: () => {
    set({ isMenuOpen: false });
  },

  showDialogue: () => {
    set({ isDialogueBoxVisible: true });
  },

  hideDialogue: () => {
    set({ isDialogueBoxVisible: false });
  },

  showChoices: () => {
    set({ isChoicePanelVisible: true });
  },

  hideChoices: () => {
    set({ isChoicePanelVisible: false });
  },

  setTransitioning: (transitioning) => {
    set({ isTransitioning: transitioning });
  },

  // === Dialogue Actions ===
  setTypewriterComplete: (complete) => {
    set({
      isTypewriterComplete: complete,
      // Don't modify canAdvance here - let AudioManager control it
    });
  },

  setCanAdvance: (canAdvance) => {
    set({ canAdvance });
  },

  // === Narrative Actions (communicate with Blazor) ===
  advanceNarrative: () => {
    const { canAdvance, isTypewriterComplete } = get();

    // Skip typewriter if not complete
    if (!isTypewriterComplete) {
      set({ isTypewriterComplete: true, canAdvance: true });
      return;
    }

    // Advance to next narrative
    if (canAdvance) {
      if (window.NordicNeveInterop) {
        window.NordicNeveInterop.notifyAdvanceNarrative();
      }
    }
  },

  selectChoice: (choiceId) => {
    console.log('[StoryStore] Choice selected:', choiceId);

    // Hide choices immediately
    set({
      isChoicePanelVisible: false,
      isTransitioning: true,
    });

    // Notify Blazor
    if (window.NordicNeveInterop) {
      window.NordicNeveInterop.notifyChoiceSelected(choiceId);
    }
  },

  // === Utility ===
  reset: () => {
    set(initialState);
  },
}));
